import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useAuth } from '@clerk/nextjs';
import { getLogger } from '@lib/observability/logging/log.client';
import { isProductionBuild } from '@lib/shared/utils/is-production-build';
import { instrumentSupabasePrototypeOnce } from '@lib/supabase/utils/instrument-supabase-prototype-once';
import { PostgrestClient } from '@supabase/postgrest-js';
import { StorageClient } from '@supabase/storage-js';
import type { Database } from './db.types';
import { type DbClient, attachStorage } from './types';
const {
  logError
} = getLogger('lib/supabase/client');

// Routes browser Supabase calls through a same-origin Next.js
// rewrite (see `next.config.js` → /_supabase/:path*) when the
// configured URL points at a loopback host. Without this, an
// HTTPS page (e.g. served by portless) hitting
// http://127.0.0.1:54321 is blocked as mixed content and the
// browser surfaces the fetch TypeError as
// `error.message === 'Load failed'`. Gated on
// NODE_ENV !== 'production' *and* a loopback host so the proxy
// path can never carry production traffic.
function resolveSupabaseUrl(): string {
  const configured = process.env.NEXT_PUBLIC_SUPABASE_URL!;
  if (typeof window === 'undefined') return configured;
  if (isProductionBuild()) return configured;
  let host = '';
  try {
    host = new URL(configured).hostname;
  } catch {
    return configured;
  }
  if (host !== 'localhost' && host !== '127.0.0.1') return configured;
  return `${window.location.origin}/_supabase`;
}

/**
 * Supabase client for React client components. The Clerk
 * session JWT is injected per request via a custom fetch shared
 * between Postgrest and Storage, so DB and storage RLS resolve
 * against the same identity.
 */
export function useSupabaseClient() {
  const $ = _c(5);
  const {
    getToken
  } = useAuth();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = resolveSupabaseUrl();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const baseUrl = t0;
  const restUrl = `${baseUrl}/rest/v1`;
  const storageUrl = `${baseUrl}/storage/v1`;
  const apikey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
  let t1;
  if ($[1] !== getToken) {
    t1 = async (input, t2) => {
      const init = t2 === undefined ? {} : t2;
      const token = await getToken();
      const headers = new Headers(init.headers);
      headers.set("apikey", apikey);
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      return fetch(input, {
        ...init,
        headers
      });
    };
    $[1] = getToken;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const customFetch = t1 as typeof fetch;
  let client;
  if ($[3] !== customFetch) {
    const pg = new PostgrestClient(restUrl, {
      headers: {
        apikey
      },
      fetch: customFetch
    });
    client = attachStorage(pg, () => new StorageClient(storageUrl, {
      apikey
    }, customFetch));
    instrumentSupabasePrototypeOnce(client, logError);
    $[3] = customFetch;
    $[4] = client;
  } else {
    client = $[4];
  }
  return client;
}